import { useMemo, useState } from "react";
import { Loader2, CalendarCheck, CheckCircle2, Phone, Video, MapPin } from "lucide-react";
import { Reveal } from "@/components/launchx/Reveal";
import { useAgencyLead, BUDGETS } from "./useAgencyLead";

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none focus:border-electric focus:ring-2 focus:ring-electric/15 transition-all";
const labelCls = "block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2";
const MODES = [
  { id: "Phone Call", icon: Phone },
  { id: "Video Call", icon: Video },
  { id: "In-Person (Rampur)", icon: MapPin },
];

export const AgencyConsultation = ({ content }) => {
  const { form, set, busy, done, submit } = useAgencyLead("consultation", { consultation_mode: "Phone Call" });
  const [mode, setMode] = useState("request");
  const slots = content?.consultation_slots || [];

  const groupedSlots = useMemo(() => {
    const g = {};
    slots.forEach((s) => { (g[s.date] = g[s.date] || []).push(s.time); });
    return Object.entries(g).slice(0, 6);
  }, [slots]);

  const submitBooking = () => {
    if (mode === "slot" && (!form.consultation_date || !form.consultation_time)) return;
    submit();
  };

  return (
    <section id="consultation" data-testid="agency-consultation" className="bg-navy py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(37,99,235,0.15),transparent_55%)]" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-cyanx">Free Consultation</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Book your 30-minute strategy call
            </h2>
            <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
              Talk to a strategist about your goals. No sales pressure — just a clear picture of what will work for your business.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 bg-white rounded-3xl p-7 sm:p-10 shadow-2xl shadow-navy-700/40" data-testid="agency-consultation-card">
            {done ? (
              <div className="text-center py-10" data-testid="agency-consultation-success">
                <CheckCircle2 size={52} className="mx-auto text-electric" />
                <h3 className="mt-5 font-display text-xl font-bold text-slate-900">Consultation requested!</h3>
                <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto">
                  Reference <span className="font-bold text-electric">{done.lead_id}</span>. We'll confirm your {form.consultation_mode === "Phone Call" ? "call" : "session"} shortly on WhatsApp.
                </p>
              </div>
            ) : (
              <form data-testid="agency-consultation-form" onSubmit={(e) => { e.preventDefault(); submitBooking(); }} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="consult-name">Your Name *</label>
                    <input id="consult-name" data-testid="consult-name-input" required className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="consult-mobile">Mobile (WhatsApp) *</label>
                    <input id="consult-mobile" data-testid="consult-mobile-input" required inputMode="numeric" maxLength={10} className={inputCls} placeholder="10-digit mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="consult-business">Business Name</label>
                    <input id="consult-business" data-testid="consult-business-input" className={inputCls} placeholder="Your business name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="consult-budget">Monthly Marketing Budget</label>
                    <select id="consult-budget" data-testid="consult-budget-select" className={inputCls} value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                      <option value="">Select budget</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Preferred mode</label>
                  <div className="flex flex-wrap gap-2" data-testid="consult-mode-group">
                    {MODES.map((m) => (
                      <button type="button" key={m.id} data-testid={`consult-mode-${m.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => set("consultation_mode", m.id)}
                        className={`inline-flex items-center gap-2 text-xs font-semibold rounded-full px-4 py-2.5 border transition-colors ${form.consultation_mode === m.id ? "bg-navy border-navy text-white" : "border-slate-200 text-slate-600 hover:border-electric/50"}`}>
                        <m.icon size={13} /> {m.id}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Schedule</label>
                  <div className="flex gap-2" data-testid="consult-schedule-group">
                    <button type="button" data-testid="consult-schedule-request" onClick={() => { setMode("request"); set("consultation_date", null); set("consultation_time", null); }}
                      className={`flex-1 text-xs font-semibold rounded-xl px-4 py-3 border transition-colors ${mode === "request" ? "bg-electric border-electric text-white" : "border-slate-200 text-slate-600"}`}>
                      Request a callback — you confirm the time
                    </button>
                    <button type="button" data-testid="consult-schedule-slot" onClick={() => setMode("slot")} disabled={groupedSlots.length === 0}
                      className={`flex-1 text-xs font-semibold rounded-xl px-4 py-3 border transition-colors disabled:opacity-40 ${mode === "slot" ? "bg-electric border-electric text-white" : "border-slate-200 text-slate-600"}`}>
                      Pick an available slot
                    </button>
                  </div>
                  {mode === "slot" && groupedSlots.length > 0 && (
                    <div className="mt-4 space-y-3" data-testid="consult-slots-list">
                      {groupedSlots.map(([date, times]) => (
                        <div key={date}>
                          <p className="text-xs font-bold text-slate-500 mb-1.5">{new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
                          <div className="flex flex-wrap gap-2">
                            {times.map((t) => (
                              <button type="button" key={t} data-testid={`consult-slot-${date}-${t.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                                onClick={() => { set("consultation_date", date); set("consultation_time", t); }}
                                className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${form.consultation_date === date && form.consultation_time === t ? "bg-navy border-navy text-white" : "border-slate-200 text-slate-600 hover:border-electric/50"}`}>
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {mode === "request" && (
                    <div className="mt-4">
                      <label className={labelCls} htmlFor="consult-time-pref">Best time to reach you</label>
                      <input id="consult-time-pref" data-testid="consult-time-pref-input" className={inputCls} placeholder="e.g. Tomorrow afternoon, after 5 PM" value={form.contact_time} onChange={(e) => set("contact_time", e.target.value)} />
                    </div>
                  )}
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="consult-consent-label">
                  <input type="checkbox" data-testid="consult-consent-checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-electric" />
                  <span className="text-xs text-slate-500 leading-relaxed">I agree to be contacted by LaunchX Agency about my enquiry and accept the Privacy Policy and Terms.</span>
                </label>
                <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <button type="submit" data-testid="consult-submit-button" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-dark disabled:opacity-60 text-white font-semibold text-sm rounded-xl px-7 py-4 transition-all hover:shadow-lg hover:shadow-electric/40">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />} Book Free Consultation
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
